/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom, Subscription} from 'rxjs';
import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {resourceOptionParams} from '../../common/controller/signal-admin-resource-api';
import {TranslateService} from '@ngx-translate/core';
import {NgForm} from '@angular/forms';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {TabsModule} from 'ngx-bootstrap/tabs';
import * as _ from 'lodash';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import template from './network-page.component.html';

import {AdServerConfig} from '../../config/api-config';
import {formatAdnStat} from '../../common/filters/format-adn-stat';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {DEFAULT_BID_SPECIFICATION_FIELDS, DEFAULT_MARKETPLACE_BID_SPECIFICATION_FIELDS, DEFAULT_MARKETPLACE_OBJECTIVE_FIELDS, DEFAULT_OBJECTIVE_FIELDS, LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {
  clearFormValidation,
  createFormSubmitState,
  exposeFormSubmitStateAccessors,
  FormControlError,
  FormSubmitState,
  handleApiValidationErrors,
  hasFormValidationError,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from '../../common/controller/form-submit-state';
import {MarketplaceProductApiService} from '../marketplace-product/marketplace-product-api.service';
import {NetworkApiService} from './network-api.service';
import {NetworkProfileApiService} from '../../../components/session/network-profile-api.service';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {TierApiService} from '../tier/tier-api.service';
import {AvailableCurrenciesApiService} from '../../common/controller/available-currencies-api.service';
import {AppSharedModule} from '../../common/app-shared.module';
import {ChartsModule} from '../../common/directives/charts.module';
import {AdnCountrySelectComponent} from '../../common/directives/country-select/adn-country-select.component';
import {FormControlsModule} from '../../common/directives/form-controls/form-controls.module';
import {CreativeAssetsModule} from '../../creative/creative-assets.module';
import {NetworkCdnTrafficComponent} from './network-cdn-traffic.component';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, ButtonsModule, TabsModule, ChartsModule, AdnCountrySelectComponent, CreativeAssetsModule, FormControlsModule, NetworkCdnTrafficComponent],
  selector: 'adn-network-page',
  template
})
export class NetworkPageComponent implements OnInit, OnDestroy {
  readonly defaultObjectiveFieldKeys = Object.keys(DEFAULT_OBJECTIVE_FIELDS);
  readonly defaultMarketplaceObjectiveFieldKeys = Object.keys(DEFAULT_MARKETPLACE_OBJECTIVE_FIELDS);
  readonly defaultBidSpecificationFieldKeys = Object.keys(DEFAULT_BID_SPECIFICATION_FIELDS);
  readonly defaultMarketplaceBidSpecificationFieldKeys = Object.keys(DEFAULT_MARKETPLACE_BID_SPECIFICATION_FIELDS);
  readonly runReportsOptions = ['REACH_ANALYSIS', 'ADVERTISER_REPORTS'];

  private readonly networkState = signal<any>(null);
  private readonly networkProfileState = signal<any>(null);
  private readonly originalNetworkState = signal<any>(null);
  private readonly originalNetworkProfileState = signal<any>(null);
  private readonly currenciesState = signal<string[]>([]);
  private readonly tiersState = signal<any[] | null>(null);
  private readonly marketplaceProductsState = signal<any[]>([]);
  private readonly pendingState = signal(false);

  get network(): any { return this.networkState(); }
  set network(value: any) { this.networkState.set(value); }
  get networkProfile(): any { return this.networkProfileState(); }
  set networkProfile(value: any) { this.networkProfileState.set(value); }
  get originalNetwork(): any { return this.originalNetworkState(); }
  set originalNetwork(value: any) { this.originalNetworkState.set(value); }
  get originalNetworkProfile(): any { return this.originalNetworkProfileState(); }
  set originalNetworkProfile(value: any) { this.originalNetworkProfileState.set(value); }
  get currencies(): string[] { return this.currenciesState(); }
  set currencies(value: string[]) { this.currenciesState.set(value); }
  get tiers(): any[] | null { return this.tiersState(); }
  set tiers(value: any[] | null) { this.tiersState.set(value); }
  get marketplaceProducts(): any[] { return this.marketplaceProductsState(); }
  set marketplaceProducts(value: any[]) { this.marketplaceProductsState.set(value); }
  get pending(): boolean { return this.pendingState(); }
  set pending(value: boolean) { this.pendingState.set(value); }
  app: string;
  isPureMarketplace = false;
  sftpHost = '';
  startDays = [
    {value: 0, text: 'Sunday'},
    {value: 1, text: 'Monday'},
    {value: 2, text: 'Tuesday'},
    {value: 3, text: 'Wednesday'},
    {value: 4, text: 'Thursday'},
    {value: 5, text: 'Friday'},
    {value: 6, text: 'Saturday'}
  ];

  submitState: FormSubmitState = createFormSubmitState();
  namedLocationRowIds: number[] = [];

  private _registeredForm: NgForm | null = null;
  private _angularFormDirty = false;
  private _formSub: Subscription | null = null;
  private _formSettleTimer: any = null;
  private _initializingForm = false;
  private _nextNamedLocationRowId = 0;
  private readonly formControlErrors: FormControlError[] = [];

  constructor(
    private readonly eventBus: AppEventBusService,
    private readonly translate: TranslateService,
    @Inject(NetworkApiService) private readonly Network: NetworkApiService,
    @Inject(NetworkProfileApiService) private readonly NetworkProfile: NetworkProfileApiService,
    @Inject(MarketplaceProductApiService) private readonly MarketplaceProduct: MarketplaceProductApiService,
    private readonly availableCurrenciesApi: AvailableCurrenciesApiService,
    private readonly tierApi: TierApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly appLocale: AppLocaleService
  ) {
    exposeFormSubmitStateAccessors(this, this.submitState, {
      getRegisteredForm: () => this._registeredForm
    });
  }

  ngOnInit(): void {
    this.app = this.localUserProfile.getCurrentApp();
    this.isPureMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();

    const env = AdServerConfig.obtain().getEnv();
    const networkTag = this.LocalNetworkProfile.getNetworkTag();
    this.sftpHost = env !== 'production'
      ? `${networkTag}.${env}.data.adnuntius.com:8164`
      : `${networkTag}.data.adnuntius.com:8164`;

    Promise.all([
      firstValueFrom(this.Network.get(this.LocalNetworkProfile.getNetworkId())),
      firstValueFrom(this.NetworkProfile.get(this.LocalNetworkProfile.getNetworkId()))
    ]).then(([network, networkProfile]) => {
      this.originalNetwork = network;
      this.originalNetworkProfile = networkProfile;
      this.initModels();
      this.loadSupportingData();
    });
  }

  ngOnDestroy(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
  }

  registerForm(editForm: NgForm): string {
    if (this._registeredForm !== editForm) {
      this._registeredForm = editForm;
      this._startTrackingForm();
    }
    return '';
  }

  submit(editForm: NgForm): void {
    if (this.pending || this.isSaveDisabled(editForm)) {
      return;
    }

    this.pending = true;
    this.submitState.ngForm = editForm;
    clearFormValidation(this.submitState, this.formControlErrors);

    firstValueFrom(this.Network.save(this.network)).then((savedNetwork: any) => {
      const network = savedNetwork && savedNetwork.defaultCurrency ? savedNetwork : this.network;

      firstValueFrom(this.NetworkProfile.save({
        id: network.id,
        name: this.network.name,
        profile: {
          adnUiLogo: this.networkProfile.profile.adnUiLogo,
          adnDefaults: this.networkProfile.profile.adnDefaults
        }
      })).then((savedNetworkProfile: any) => {
        this.originalNetwork = _.cloneDeep(network);
        this.originalNetworkProfile = _.cloneDeep(savedNetworkProfile);
        this.initModels();
        updateFormWarnings(this.submitState, network, this.LocalNetworkProfile.isPureMarketplacePlatform());

        this.LocalNetworkProfile.set('adnDefaults', savedNetworkProfile.profile.adnDefaults);
        this.LocalNetworkProfile.set('adnUiLogo', savedNetworkProfile.profile.adnUiLogo);
        this.LocalNetworkProfile.set('reportLogoUrl', network.reportLogoUrl);
        this.LocalNetworkProfile.set('availableCurrencies', network.availableCurrencies);
        this.LocalNetworkProfile.set('defaultCurrency', network.defaultCurrency);
        this.LocalNetworkProfile.set('defaultTierId', network.defaultTier.id);
        this.LocalNetworkProfile.set('tiers', network.tiers);
        this.LocalNetworkProfile.set('tagId', network.tagId);

        markFormSubmitSuccess(this.submitState, editForm);
        this._angularFormDirty = false;
        this._startTrackingForm();
        this.pending = false;
        this.eventBus.emit('SUBMIT_PROCESSED');
      }, (response: any) => {
        this.handleSaveError(response, editForm);
      });
    }, (response: any) => {
      this.handleSaveError(response, editForm);
    });
  }

  cancel(editForm: NgForm): void {
    if (this.isSaveDisabled(editForm)) {
      return;
    }
    this.initModels();
    this.submitState.validSubmission = false;
    this._angularFormDirty = false;
    editForm.control.markAsPristine();
  }

  isFormPristine(editForm: NgForm): boolean {
    void editForm;
    return !this._angularFormDirty;
  }

  isSaveDisabled(editForm: NgForm): boolean {
    return this.isFormPristine(editForm)
      || this.pending
      || (editForm.invalid && !hasFormValidationError(this.submitState));
  }

  markFormDirty(): void {
    this._registeredForm?.control.markAsDirty();
    this._angularFormDirty = true;
  }

  toggleAvailableCurrency(currency: string): void {
    const availableCurrencies = this.network.availableCurrencies || (this.network.availableCurrencies = []);
    const index = availableCurrencies.indexOf(currency);

    if (index >= 0) {
      availableCurrencies.splice(index, 1);
    } else {
      availableCurrencies.push(currency);
    }

    this.markFormDirty();
  }

  removeLocations(index: number): void {
    _.pullAt(this.networkProfile.profile.adnDefaults.namedLocations, index);
    _.pullAt(this.namedLocationRowIds, index);
    this.markFormDirty();
  }

  addLocations(): void {
    this.networkProfile.profile.adnDefaults.namedLocations.push('');
    this.namedLocationRowIds.push(this._nextNamedLocationRowId++);
    this.markFormDirty();
  }

  updateNamedLocation(index: number, countryCode: string): void {
    this.networkProfile.profile.adnDefaults.namedLocations[index] = countryCode;
    this.markFormDirty();
  }

  formatStat(value: any): string {
    return formatAdnStat(value, undefined, undefined, undefined, this.LocalNetworkProfile.get('defaultCurrency'), this.appLocale.getLocaleId());
  }

  advancedLoggingHelpText(): string {
    const key = `network.advancedLogging.${this.network.advancedLogging}.helpText`;
    return this.translate.instant(key, {sftpHost: this.sftpHost});
  }

  rawLogTypesHelpText(): string {
    return this.translate.instant('network.rawLogTypes.helpText', {sftpHost: this.sftpHost});
  }

  compareById(option: any, value: any): boolean {
    return option && value ? option.id === value.id : option === value;
  }

  trackByNamedLocation = (index: number): number => {
    return this.namedLocationRowIds[index];
  };

  private handleSaveError(response: any, editForm: NgForm): void {
    if ((response.status === 400 || response.status === 403 || response.status === 422)
      && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
      handleApiValidationErrors(
        this.submitState,
        this.network,
        _.get(response, ['data', 'errors']) || response.data,
        this.formControlErrors,
        editForm
      );
    }
    markFormSubmitError(this.submitState, editForm);
    this.pending = false;
    this.eventBus.emit('SUBMIT_PROCESSED');
  }

  private initModels(): void {
    this.network = _.cloneDeep(this.originalNetwork);
    this.networkProfile = _.cloneDeep(this.originalNetworkProfile);
    this.networkProfile.profile = this.networkProfile.profile || {};
    this.networkProfile.profile.adnDefaults = this.networkProfile.profile.adnDefaults || {};
    this.LocalNetworkProfile.sanitiseProfileData(this.networkProfile.profile.adnDefaults);
    if (this.networkProfile.profile.adnDefaults.namedLocations.length === 0) {
      this.networkProfile.profile.adnDefaults.namedLocations.push('');
    }
    this.namedLocationRowIds = _.map(this.networkProfile.profile.adnDefaults.namedLocations, () => this._nextNamedLocationRowId++);
  }

  private loadSupportingData(): void {
    firstValueFrom(this.MarketplaceProduct.listOptions(resourceOptionParams({pageSize: 505, minimal: true}))).then((data: any) => {
      this.marketplaceProducts = data.results;
    });

    this.availableCurrenciesApi.get().subscribe((currencyList) => {
      // [] → populated list flips *ngFor during the network-load CD turn; defer to avoid NG0100.
      deferSignalStateWrite(() => {
        this.currencies = _.map(currencyList, 'currency');
      });
    });

    if (this.app === 'ADVERTISING') {
      firstValueFrom(this.tierApi.listAll()).then((page: any) => {
        // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferSignalStateWrite(() => {
          this.tiers = page.results;
          _.forEach(this.tiers, (tier) => {
            tier.displayName = tier.name + (_.find(this.network.tiers, {id: tier.id}) ? '' : ' (disabled)');
          });
        });
      });
    }
  }

  private _startTrackingForm(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    this._initializingForm = true;
    this._angularFormDirty = false;
    this._formSettleTimer = setTimeout(() => {
      if (!this._registeredForm) {
        return;
      }
      this._registeredForm.control.markAsPristine();
      this._registeredForm.control.markAsUntouched();
      this._angularFormDirty = false;
      this._initializingForm = false;
      this._formSub = this._registeredForm.valueChanges?.subscribe(() => {
        if (!this._initializingForm && !this._angularFormDirty) {
          this._angularFormDirty = true;
        }
      }) || null;
    }, 500);
  }
}
